import React, { useState, useEffect } from 'react';

const MicrophonePermissionModal = ({ isOpen, onPermissionGranted, onCancel }) => {
    const [checking, setChecking] = useState(true);
    const [permissionStatus, setPermissionStatus] = useState('prompt'); // 'prompt', 'granted', 'denied'
    const [error, setError] = useState('');
    const [voiceEnabled, setVoiceEnabled] = useState(true); // Default to enabled

    useEffect(() => {
        if (isOpen) {
            // Reset state when modal opens
            setVoiceEnabled(true);
            setError('');
            setPermissionStatus('prompt');
            setChecking(true);
            // Check permission when modal opens (voice is enabled by default)
            checkMicrophonePermission();
        }
    }, [isOpen]);

    useEffect(() => {
        // When voice enabled changes while modal is open, update permission check
        if (isOpen && !checking) {
            if (!voiceEnabled) {
                setPermissionStatus('not_required');
                setError('');
            } else if (permissionStatus === 'not_required') {
                // If voice is re-enabled, check permission again
                checkMicrophonePermission();
            }
        }
    }, [voiceEnabled, isOpen, checking, permissionStatus]);

    const checkMicrophonePermission = async () => {
        setChecking(true);
        setError('');

        try {
            // Check if browser supports permissions API
            if (navigator.permissions && navigator.permissions.query) {
                try {
                    const result = await navigator.permissions.query({ name: 'microphone' });
                    setPermissionStatus(result.state);
                    
                    // If already granted, proceed immediately
                    if (result.state === 'granted') {
                        setChecking(false);
                        return;
                    }
                } catch (permError) {
                    // Permissions API not supported or failed, try direct request
                    console.warn('Permission query failed, will request directly:', permError);
                }
            }

            // Try to get microphone access to check/request permission
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                // Permission granted - stop the stream immediately (we just needed to check)
                stream.getTracks().forEach(track => track.stop());
                setPermissionStatus('granted');
            } catch (mediaError) {
                if (mediaError.name === 'NotAllowedError' || mediaError.name === 'PermissionDeniedError') {
                    setPermissionStatus('denied');
                    setError('Microphone access was denied. Please enable microphone permissions in your browser settings to use voice features.');
                } else {
                    setPermissionStatus('denied');
                    setError('Failed to access microphone. Please check your browser settings.');
                }
            }
        } catch (err) {
            console.error('Error checking microphone permission:', err);
            setError('An error occurred while checking microphone permissions.');
            setPermissionStatus('denied');
        } finally {
            setChecking(false);
        }
    };

    const handleRequestPermission = async () => {
        if (!voiceEnabled) {
            // If voice is disabled, just proceed
            sessionStorage.setItem('chefVoiceEnabled', 'false');
            if (onPermissionGranted) {
                onPermissionGranted();
            }
            return;
        }

        setChecking(true);
        setError('');

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            // Permission granted - stop the stream immediately (we just needed permission)
            stream.getTracks().forEach(track => track.stop());
            setPermissionStatus('granted');
            // Store voice preference
            sessionStorage.setItem('chefVoiceEnabled', 'true');
            // Call the callback to proceed
            if (onPermissionGranted) {
                onPermissionGranted();
            }
        } catch (mediaError) {
            if (mediaError.name === 'NotAllowedError' || mediaError.name === 'PermissionDeniedError') {
                setPermissionStatus('denied');
                setError('Microphone access was denied. Please click "Allow" when prompted, or enable microphone permissions in your browser settings.');
            } else if (mediaError.name === 'NotFoundError') {
                setError('No microphone found. Please connect a microphone and try again.');
            } else {
                setError('Failed to access microphone. Please check your browser settings and try again.');
            }
        } finally {
            setChecking(false);
        }
    };

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        }
    };

    if (!isOpen) return null;

    return (
        <div 
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50"
            onClick={handleCancel}
        >
            <div 
                className="bg-neutral-0 hairline p-6 max-w-md w-full mx-4 relative"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button */}
                <button
                    onClick={handleCancel}
                    className="absolute top-4 right-4 text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0"
                    aria-label="Close"
                >
                    ×
                </button>

                <h2 className="font-sans text-2xl font-medium text-neutral-900 mb-4">
                    Enable Chef Voice?
                </h2>

                <p className="font-sans text-base text-neutral-900 opacity-80 mb-4">
                    Would you like to enable Chef Voice? This allows you to use voice commands to control timers, navigate steps, and ask cooking questions.
                </p>
                
                <div className="mb-6 p-4 bg-neutral-50 hairline">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={voiceEnabled}
                            onChange={(e) => setVoiceEnabled(e.target.checked)}
                            className="w-5 h-5 text-neutral-900 border-neutral-900 focus:ring-neutral-900"
                        />
                        <span className="font-sans text-sm text-neutral-900">
                            Enable Chef Voice (requires microphone access)
                        </span>
                    </label>
                    {voiceEnabled && (
                        <p className="font-sans text-xs text-neutral-900 opacity-60 mt-2 ml-8">
                            Say <strong>"Hey Chef"</strong> to control timers, navigate steps, and ask questions.
                        </p>
                    )}
                </div>

                {checking ? (
                    <div className="flex items-center gap-3 mb-6">
                        <svg className="animate-spin h-5 w-5 text-neutral-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="font-sans text-sm text-neutral-900 opacity-80">
                            Checking microphone permissions...
                        </span>
                    </div>
                ) : (permissionStatus === 'granted' || (!voiceEnabled && permissionStatus === 'not_required')) ? (
                    <div className="mb-6">
                        {voiceEnabled ? (
                            <div className="flex items-center gap-3 mb-4">
                                <div className="text-2xl">✓</div>
                                <p className="font-sans text-base text-neutral-900">
                                    Microphone permission granted! Chef Voice is enabled.
                                </p>
                            </div>
                        ) : (
                            <div className="mb-4">
                                <p className="font-sans text-base text-neutral-900">
                                    Chef Voice is disabled. You can enable it later in the session.
                                </p>
                            </div>
                        )}
                        <button
                            onClick={() => {
                                // Store voice preference in sessionStorage
                                sessionStorage.setItem('chefVoiceEnabled', voiceEnabled.toString());
                                if (onPermissionGranted) {
                                    onPermissionGranted();
                                }
                            }}
                            className="w-full px-4 py-2 font-mono text-sm uppercase tracking-wider transition-opacity duration-120"
                            style={{ 
                                backgroundColor: '#1b263b', 
                                color: '#FFFFFF' 
                            }}
                        >
                            Continue to Live Session
                        </button>
                    </div>
                ) : (
                    <>
                        {error && (
                            <div className="bg-red-50 p-3 hairline border-red-200 mb-4">
                                <p className="font-sans text-sm text-red-900 font-mono">
                                    {error}
                                </p>
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={handleCancel}
                                disabled={checking}
                                className="flex-1 px-4 py-2 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            {!voiceEnabled ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        // Store voice preference
                                        sessionStorage.setItem('chefVoiceEnabled', 'false');
                                        if (onPermissionGranted) {
                                            onPermissionGranted();
                                        }
                                    }}
                                    className="flex-1 px-4 py-2 font-mono text-sm uppercase tracking-wider transition-opacity duration-120"
                                    style={{ 
                                        backgroundColor: '#1b263b', 
                                        color: '#FFFFFF' 
                                    }}
                                >
                                    Continue Without Voice
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleRequestPermission}
                                    disabled={checking}
                                    className="flex-1 px-4 py-2 font-mono text-sm uppercase tracking-wider transition-opacity duration-120 disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{ 
                                        backgroundColor: '#1b263b', 
                                        color: '#FFFFFF' 
                                    }}
                                >
                                    {checking ? 'Requesting...' : 'Allow Microphone'}
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default MicrophonePermissionModal;

